import { Role } from '../types/auth';
import { AuthContextType } from '../hooks/useAuth';

export type OperatorOption = { id: string; name: string };
type RawOperator = { id: string; name?: string; surName?: string };

/**
 * Opciones para el dropdown "Operario que registra": operarios activos del
 * tenant (misma fuente que la cuadrilla, via `selectActiveOperatorsAndVehicles`)
 * + el usuario logueado si NO es rol Tablet — así el pre-relleno del campo es
 * visible/seleccionable para cuentas individuales aunque no sean "operarios".
 */
export const buildRegisteredByOperators = (
  operators: RawOperator[] | undefined,
  userData: AuthContextType | null,
): OperatorOption[] => {
  const options: OperatorOption[] = (operators ?? []).map((op) => ({
    id: op.id,
    name: `${op.name ?? ''} ${op.surName ?? ''}`.trim(),
  }));

  const role = userData?.user?.user?.rolActive;
  const meId = userData?.user?.user?.id;
  if (role !== Role.Tablet && meId && !options.some((o) => o.id === meId)) {
    options.unshift({
      id: meId,
      name: `${userData?.user?.user?.name ?? ''} ${
        userData?.user?.user?.surName ?? ''
      }`.trim(),
    });
  }
  return options;
};

/**
 * Nombre completo para pintar en una ficha. Puro: recibe el usuario ya
 * resuelto y devuelve `''` si no hay — el llamante decide si oculta la fila.
 */
export const formatUserFullName = (
  user: RawOperator | null | undefined,
): string => (user ? `${user.name ?? ''} ${user.surName ?? ''}`.trim() : '');

/**
 * El valor de «Registrado por» con el que puede ARRANCAR el formulario: el
 * id si está entre las opciones del desplegable, y `''` si no.
 *
 * La básica del RV nace con la cuenta de la tablet como operario que
 * registra (el backend lo exige al crear y en ese momento no hay pantalla
 * donde elegirlo), pero esa cuenta es un dispositivo, no una persona, y
 * `buildRegisteredByOperators` la deja fuera a propósito. Hasta 2026-09-04
 * el formulario recibía ese id por debajo: el desplegable salía vacío, la
 * validación pasaba y nadie elegía a la persona. Descartando lo que no se
 * puede elegir, el campo queda vacío, se pone en rojo y hay que elegir a
 * alguien de verdad — que es lo que el usuario esperaba ver.
 */
export const selectableRegisteredBy = (
  value: string | null | undefined,
  options: OperatorOption[] | undefined,
): string =>
  value && (options ?? []).some((o) => o.id === value) ? value : '';

type KnownUser = RawOperator & { rolActive?: string | null };

/**
 * Con qué valor y con qué opciones arranca «Registrado por».
 *
 *  - El id está entre las opciones → se conserva.
 *  - No está, pero es una PERSONA conocida (un operario dado de baja que
 *    registró la actuación en su día) → se conserva y se añade a las opciones
 *    para que el desplegable enseñe su nombre: quien valida no tiene por qué
 *    reescribir quién registró solo porque ese operario ya no esté.
 *  - No está y es la cuenta de una tablet, o un id que nadie conoce → `''`.
 *    La cuenta de la tablet es un dispositivo, no una persona; el campo queda
 *    vacío, se pone en rojo y hay que elegir a alguien de verdad.
 *
 * 🔴 …salvo que NO HAYA OPCIONES. Sin cobertura el catálogo de operarios no
 * carga (`selectActiveOperatorsAndVehicles` va `network-only` y su fallo cae
 * a una lista vacía), así que vaciar el campo dejaría al operario con un
 * obligatorio en rojo y un desplegable sin nada que elegir: la básica no se
 * podría crear en ruta, que es justo para lo que existe. Sin alternativa que
 * ofrecer se conserva lo que venga. Cazado por la CI en el módulo `offline`.
 *
 * `knownUsers` es la lista de usuarios ya cacheada (`FetchUsers`); sin ella
 * solo se distingue elegible de no elegible.
 */
export const resolveRegisteredBy = (
  value: string | null | undefined,
  options: OperatorOption[] | undefined,
  knownUsers: KnownUser[] | undefined,
): { value: string; options: OperatorOption[] } => {
  const base = options ?? [];
  if (!value) return { value: '', options: base };
  if (base.some((o) => o.id === value)) return { value, options: base };
  if (base.length === 0) return { value, options: base };

  const known = (knownUsers ?? []).find((u) => u.id === value);
  if (known && known.rolActive !== Role.Tablet) {
    return {
      value,
      options: [...base, { id: known.id, name: formatUserFullName(known) }],
    };
  }
  return { value: '', options: base };
};
